import * as React from 'react';
import { shallowCompare, memoizeFunction } from 'office-ui-fabric-react';
import { Resource, createResourceCalendar, DailyUsage, filterByDatesUsage, ResourceUsage, findUsage, getDatesBetween } from '../../../../store/ResourcesListStore';
import { arraysEqual, HUNDRED_PCT, notEmpty, roundToHundreds, toDate } from '../../../utils/common';
import { getWorkingHoursBetweenDates } from '../../../common/timeline/utils';
import { tryGetTimephasedValueFromResource } from '../../../../services/TimephasedValueService';
import { getDefaultCostRateTable } from '../../../../store/selectors/costRateTableSelectors';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { Dictionary, entityLogoConfig, EntityType, EstimatedChargeCalculationMode, mapServerEntityType, MaybeDate, Quantization, ServerEntityType } from '../../../../entities/common';
import { ViewType } from '../ResourceUsageGrid';
import SelectionExt from '../../../common/SelectionExt';
import EditMode from './CellEditMode';
import ViewMode from './CellViewMode';
import { CellSummaryContext, CellSummaryData, HoursData, TimeType, TimeTypesConfig } from './common';
import { CalendarDataSet } from '../../../../store/calendar/common';
import { getEntityCalendar } from '../../../../store/CalendarsListStore';

interface StateProps {
    calendar: CalendarDataSet;
    resourcePlanningLevel: EntityType;
    defaultCostRateTableId?: string;
}

interface OwnProps {
    entityId?: string;
    entityType: EntityType;
    selection?: SelectionExt;
    selectionIndex?: number;
    onClick?: React.MouseEventHandler;
    onKeyDown?: React.KeyboardEventHandler;
    onKeyPress?: React.KeyboardEventHandler;
    onFocus?: React.FocusEventHandler;
    viewType: ViewType;
    resource: Resource;
    quantization: Quantization;
    prevStartDate: Date | undefined;
    startDate: Date;
    finishDate: Date;
    onEditComplete?: (value: number | null, timeType: TimeType) => void;
    onEditStart?: () => void;
    componentRef?: React.LegacyRef<HTMLDivElement>;
    timeType: TimeType[];
    summaryCalculator: (startDate: Date, finishDate: Date, context: CellSummaryContext) => CellSummaryData;
    timeTypesConfig: TimeTypesConfig;
}

type Props = StateProps & OwnProps;

interface State {
    isSelected?: boolean;
    summaryData: CellSummaryData;
    prevHours: HoursData | undefined;
    editTimeType?: TimeType;
}

const calculateSummaryData = (props: Props): CellSummaryData => props.summaryCalculator(props.startDate, props.finishDate, props);
const calculatePrevHours = (props: Props): HoursData | undefined => props.prevStartDate
    ? props.summaryCalculator(props.prevStartDate, props.startDate.clone().addDays(-1), props).hours
    : undefined;
const calculateIsSelected = (props: Props): boolean | undefined => props.selectionIndex !== undefined ? props.selection?.isIndexSelected(props.selectionIndex) : undefined

const getMaxCapacityHours = (quantization: Quantization): number => {
    return {
        [Quantization.days]: 8,
        [Quantization.weeks]: 40,
        [Quantization.months]: 160,
        [Quantization.quarters]: 480,
        [Quantization.years]: 1920
    }[quantization] ?? 0;
}

class ResourceUsageCell extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = this._buildState(props);

        if (props.selectionIndex !== undefined) {
            this.props.selection?.AddEventHandler(this._onSelectionChanged, props.selectionIndex);
        }
    }

    componentWillUnmount() {
        this.props.selection?.RemoveEventHandler(this._onSelectionChanged);
    }

    componentWillReceiveProps(nextProps: Props) {
        if (this.props.selectionIndex !== nextProps.selectionIndex) {
            if (this.props.selectionIndex !== undefined) {
                nextProps.selection?.RemoveEventHandler(this._onSelectionChanged);
            }
            if (nextProps.selectionIndex !== undefined) {
                nextProps.selection?.AddEventHandler(this._onSelectionChanged, nextProps.selectionIndex);
            }
        }

        if (this.props.resource !== nextProps.resource
            || this.props.prevStartDate?.getTime() !== nextProps.prevStartDate?.getTime()
            || this.props.startDate.getTime() !== nextProps.startDate.getTime()
            || this.props.finishDate.getTime() !== nextProps.finishDate.getTime()
            || this.props.calendar !== nextProps.calendar
            || this.props.quantization !== nextProps.quantization
            || !arraysEqual(this.props.timeType, nextProps.timeType)
            || this.props.defaultCostRateTableId !== nextProps.defaultCostRateTableId
            || this.props.summaryCalculator !== nextProps.summaryCalculator
        ) {
            this.setState(this._buildState(nextProps));
        }
    }

    private _onSelectionChanged = () => {
        const isSelected = this.props.selection!.isIndexSelected(this.props.selectionIndex!);
        if (this.state.isSelected !== isSelected) {
            this.setState({ isSelected });
        }
    }

    shouldComponentUpdate(nextProps: Props, nextState: State) {
        return !shallowCompare(this.state, nextState)
            || !shallowCompare(this._getPropsToCompare(this.props), this._getPropsToCompare(nextProps));
    }

    private _getPropsToCompare(props: Props) {
        return {
            resource: props.resource,
            startDate: props.startDate.getTime(),
            finishDate: props.finishDate.getTime(),
            calendar: props.calendar,
            viewType: props.viewType,
            quantization: props.quantization,
            defaultCostRateTableId: props.defaultCostRateTableId,
            timeTypesConfig: props.timeTypesConfig
        }
    }

    render() {
        const { selectionIndex, viewType, onClick, onFocus, onKeyDown, onKeyPress, timeType, quantization } = this.props;
        const { editTimeType, isSelected, summaryData, prevHours } = this.state;

        const isEditable = selectionIndex !== undefined;
        const isEditMode = editTimeType !== undefined;

        const className = [
            'resource-usage-cell',
            summaryData.hours.capacity ? undefined : 'no-capacity',
            ViewType[viewType],
            isSelected ? 'selected' : undefined,
            summaryData.hasLocalProposedOverage && !isEditMode ? 'has-local-proposed-overage' : undefined,
            summaryData.hasLocalOverage && !isEditMode ? 'has-local-overage' : undefined,
            summaryData.hasProposedOverage && !isEditMode ? 'has-proposed-overage' : undefined,
            summaryData.hasOverage && !isEditMode ? 'has-overage' : undefined,
            isEditMode ? 'edit-mode' : undefined,
            'fill-space'
        ].filter(notEmpty).join(' ');

        return <div
            tabIndex={isEditable ? 0 : undefined}
            ref={this.props.componentRef}
            data-selection-index={selectionIndex}
            onClick={onClick}
            onFocus={onFocus}
            onDoubleClick={isEditable ? () => this._startEditing() : undefined}
            onKeyDown={onKeyDown}
            onKeyPress={isEditable ? e => {
                if (!isEditMode && (e.key === 'Enter' || e.charCode >= 48 && e.charCode <= 57)) {
                    this._startEditing();
                }
                onKeyPress?.(e)
            } : undefined}
            className={className}>
            <div className={`cell-content fill-space columns-${timeType.length}`}>
                {isEditMode
                    ? <EditMode
                        editTimeType={editTimeType!}
                        summaryData={summaryData}
                        maxCapacityHours={getMaxCapacityHours(quantization)}
                        viewType={viewType}
                        onChangeSummaryData={_ => this.setState({ summaryData: _ })}
                        onEditComplete={this._onEditComplete} />
                    : <ViewMode _startEditing={this._startEditing}
                        summaryData={summaryData}
                        prevHours={prevHours}
                        maxCapacityHours={getMaxCapacityHours(quantization)}
                        viewType={viewType}
                        timeType={timeType}
                        timeTypesConfig={this.props.timeTypesConfig} />}
            </div>
        </div>;
    }

    private _startEditing = (editTimeType?: TimeType) => {
        if (editTimeType === undefined) {
            const { timeType } = this.props;
            if (timeType.includes('plan') && !timeType.includes('actual')) {
                editTimeType = 'plan';
            } else if (!timeType.includes('plan') && timeType.includes('actual')) {
                editTimeType = 'actual';
            }
        }

        if (editTimeType === undefined || !this.props.onEditStart) {
            return;
        }

        this.props.onEditStart?.();
        this.setState({ editTimeType });
    }

    private _onEditComplete = (_: number | null) => {
        this.props.onEditComplete?.(_, this.state.editTimeType!);
        this.setState({ editTimeType: undefined });
    }

    private _buildState(props: Props): State {
        return {
            isSelected: calculateIsSelected(props),
            summaryData: calculateSummaryData(props),
            prevHours: calculatePrevHours(props)
        };
    }
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    return {
        calendar: getEntityCalendar(state.calendars, ownProps.resource, ownProps.resource.calendar.id),
        resourcePlanningLevel: mapServerEntityType[state.tenant.resourcePlanningSettings?.resourcePlanningLevel!]!,
        defaultCostRateTableId: getDefaultCostRateTable(state)?.id,
    };
}

export default connect(mapStateToProps)(ResourceUsageCell);

export interface ISummaryInfo {
    fullFteCapacityHours: number,
    capacityHours: number,
    capacityByDay: Dictionary<number>,
    committedWork: number,
    proposedWork: number,
    actualWork: number,

    committedByEntity: Dictionary<number>,
    proposedByEntity: Dictionary<number>,
    actualByEntity: Dictionary<number>,
    availability: number,
    overage: number,
    hasLocalOverage: boolean,

    estimatedCharge: number,
    estimatedCost: number,
    actualCost: number,
    estimatedChargeByEntity: Dictionary<number>,
    estimatedCostByEntity: Dictionary<number>,
    actualCostByEntity: Dictionary<number>,

    effectiveUsage: DailyUsage | undefined,
    effectiveUsageByEntity: Dictionary<DailyUsage>,

    proposed_overage: number,
    proposed_hasLocalOverage: boolean,
    proposed_overage_byEntity: Dictionary<number>,
    proposed_hasLocalOverage_byEntity: Dictionary<boolean>,
}

export function calculateSummary(resource: Resource, startDate: Date, finishDate: Date, baseResourceCalendar: CalendarDataSet, entityType: EntityType, resourcePlanningLevel: EntityType, defaultCostRateTableId?: string)
    : ISummaryInfo {
    return calculateSummaryMemoized(resource, startDate.getTime(), finishDate.getTime(), baseResourceCalendar, entityType, resourcePlanningLevel, defaultCostRateTableId);
}

const calculateSummaryMemoized = memoizeFunction((resource: Resource, startDate: number, finishDate: number, baseResourceCalendar: CalendarDataSet, entityType: EntityType,
    resourcePlanningLevel: EntityType, defaultCostRateTableId?: string) => calculateSummaryImpl(resource, new Date(startDate), new Date(finishDate), baseResourceCalendar, entityType, resourcePlanningLevel, defaultCostRateTableId));

const getWorkingHoursBetweenDatesMemoized = memoizeFunction((startDate: Date, endDate: Date, calendar: CalendarDataSet) =>
    getWorkingHoursBetweenDates(startDate, endDate, calendar));

const createResourceCalendarMemoized = memoizeFunction((resource: Resource, baseResourceCalendar: CalendarDataSet) =>
    createResourceCalendar(resource, baseResourceCalendar));

function calculateSummaryImpl(resource: Resource, startDate: Date, finishDate: Date, baseResourcecalendar: CalendarDataSet, entityType: EntityType, resourcePlanningLevel: EntityType, defaultCostRateTableId?: string)
    : ISummaryInfo {
    const resourceCalendar = createResourceCalendarMemoized(resource, baseResourcecalendar);
    const fullFteCapacityHours = getWorkingHoursBetweenDatesMemoized(startDate, finishDate, baseResourcecalendar);
    const isSingleDay = startDate.getBeginOfDay().getTime() === finishDate.getBeginOfDay().getTime();

    const getCapacityHours = (date: Date, maxUnitsPct?: number, nonProjectWorkPct?: number): number => {
        const maxUnitsMultiplier = (maxUnitsPct ?? 0) / HUNDRED_PCT;
        const projectWorkMultiplier = (HUNDRED_PCT - (nonProjectWorkPct ?? 0)) / HUNDRED_PCT;
        return roundToHundreds(date.getWorkingHours(resourceCalendar) * maxUnitsMultiplier * projectWorkMultiplier);
    }

    const checkEntityType = (usage: DailyUsage) => EntityType[ServerEntityType[usage.entityType]] === entityType
        || entityType === EntityType.Idea && EntityType[ServerEntityType[usage.entityType]] === resourcePlanningLevel;

    const plan = filterByDatesUsage(resource.usage, startDate, finishDate)
        .filter(_ => checkEntityType(_))
        .reduce((cum, _) => {
            const dailyKey = `${_.date.getTime()}`;
            const dailyPlan = cum.dailyPlan[dailyKey] ?? { date: _.date, committed: 0, proposed: 0, proposedByEntity: {},
                    capacityHours: getCapacityHours(_.date, _.maxUnitsPct, _.nonProjectWorkPct) };
            dailyPlan.committed = dailyPlan.committed + (_.committed ? _.plannedHours : 0);
            dailyPlan.proposed = dailyPlan.proposed + (!_.committed ? _.plannedHours : 0);
            dailyPlan.proposedByEntity[_.entityId] = (dailyPlan.proposedByEntity[_.entityId] ?? 0) + (!_.committed ? _.plannedHours : 0);

            const entityUsageAttrs = resource.usage?.byEntity?.[_.entityId]?.attributes;
            const mode = entityUsageAttrs?.estimatedChargeCalculationMode ?? EstimatedChargeCalculationMode.UseChargeRate;
            const chargeRate = entityUsageAttrs?.chargeRate ?? _.rate ?? 0;
            const standardCost = _.standardCost ?? 0;
            const standardRate = _.standardRate ?? 0;

            const estimatedCharge = _.plannedHours * (mode === EstimatedChargeCalculationMode.UseChargeRate ? chargeRate : standardRate);
            const estimatedCost = _.plannedHours * standardCost;
            const actualCost = _.actualHours * standardCost;

            return {
                committedByEntity: { ...cum.committedByEntity, [_.entityId]: (cum.committedByEntity[_.entityId] ?? 0) + (_.committed ? _.plannedHours : 0) },
                proposedByEntity: { ...cum.proposedByEntity, [_.entityId]: (cum.proposedByEntity[_.entityId] ?? 0) + (!_.committed ? _.plannedHours : 0) },
                actualByEntity: { ...cum.actualByEntity, [_.entityId]: (cum.actualByEntity[_.entityId] ?? 0) + _.actualHours },
                estimatedCharge: cum.estimatedCharge + estimatedCharge,
                estimatedCost: cum.estimatedCost + estimatedCost,
                actualCost: cum.actualCost + actualCost,
                estimatedChargeByEntity: { ...cum.estimatedChargeByEntity, [_.entityId]: (cum.estimatedChargeByEntity[_.entityId] ?? 0) + estimatedCharge },
                estimatedCostByEntity: { ...cum.estimatedCostByEntity, [_.entityId]: (cum.estimatedCostByEntity[_.entityId] ?? 0) + estimatedCost },
                actualCostByEntity: { ...cum.actualCostByEntity, [_.entityId]: (cum.actualCostByEntity[_.entityId] ?? 0) + actualCost },
                effectiveUsage: isSingleDay ? (cum.effectiveUsage ?? _) : cum.effectiveUsage,
                effectiveUsageByEntity: isSingleDay ? { ...cum.effectiveUsageByEntity, [_.entityId]: _ } : cum.effectiveUsageByEntity,
                dailyPlan: { ...cum.dailyPlan, [dailyKey]: dailyPlan }
            };
        }, {
            committedByEntity: {} as Dictionary<number>,
            proposedByEntity: {} as Dictionary<number>,
            actualByEntity: {} as Dictionary<number>,
            estimatedCharge: 0,
            estimatedCost: 0,
            actualCost: 0,
            estimatedChargeByEntity: {} as Dictionary<number>,
            estimatedCostByEntity: {} as Dictionary<number>,
            actualCostByEntity: {} as Dictionary<number>,
            effectiveUsage: undefined,
            effectiveUsageByEntity: {} as Dictionary<DailyUsage>,
            dailyPlan: {} as Dictionary<{ date: Date, committed: number, proposed: number, proposedByEntity: Dictionary<number>, capacityHours: number }>
        });

    //calculate capacity for days without usage
    const dailyPlanWithDefaults = getDatesBetween(startDate, finishDate)
        .reduce((cum, date) => {
            const dailyKey = `${date.getTime()}`;
            if (cum[dailyKey] !== undefined) {
                return cum;
            }

            const maxUnitsPct = tryGetTimephasedValueFromResource('MaxUnitsPct', resource, date) ?? 0;
            const nonProjectWorkPct = tryGetTimephasedValueFromResource('NonProjectWorkPct', resource, date) ?? 0;

            return {...cum, [dailyKey]: { date, committed: 0, proposed: 0, proposedByEntity: {},
                capacityHours: getCapacityHours(date, maxUnitsPct, nonProjectWorkPct)}
            };
        }, plan.dailyPlan);

    const singleDate = startDate.getBeginOfDay();
    const effectiveUsageByEntity = isSingleDay && resource.usage
        ? Object.keys(resource.usage.byEntity).reduce((cum, entityId) => {
            if (plan.effectiveUsageByEntity[entityId] !== undefined) {
                return cum;
            }

            const costRateTableId = resource.usage?.byEntity[entityId]?.attributes.costRateTableId;
            return {
                ...cum,
                [entityId]: {
                    standardRate: tryGetTimephasedValueFromResource('StandardRate', resource, singleDate, costRateTableId) ?? 0,
                    standardCost: tryGetTimephasedValueFromResource('StandardCost', resource, singleDate, costRateTableId) ?? 0,
                    maxUnitsPct: tryGetTimephasedValueFromResource('MaxUnitsPct', resource, singleDate) ?? 0,
                    nonProjectWorkPct: tryGetTimephasedValueFromResource('NonProjectWorkPct', resource, singleDate) ?? 0,
                } as DailyUsage
            };
        }, plan.effectiveUsageByEntity)
        : plan.effectiveUsageByEntity;

    const effectiveUsage = isSingleDay && plan.effectiveUsage === undefined
        ? (Object.values(effectiveUsageByEntity)[0] ?? {
            //resource does not assigned to any entity
            standardRate: tryGetTimephasedValueFromResource('StandardRate', resource, singleDate, defaultCostRateTableId) ?? 0,
            standardCost: tryGetTimephasedValueFromResource('StandardCost', resource, singleDate, defaultCostRateTableId) ?? 0,
            maxUnitsPct: tryGetTimephasedValueFromResource('MaxUnitsPct', resource, singleDate) ?? 0,
            nonProjectWorkPct: tryGetTimephasedValueFromResource('NonProjectWorkPct', resource, singleDate) ?? 0,
        } as DailyUsage)
        : plan.effectiveUsage;

    const capacityHours = roundToHundreds(Object.keys(dailyPlanWithDefaults).reduce((sum, k) => sum + dailyPlanWithDefaults[k].capacityHours, 0));
    const committedWork = roundToHundreds(Object.keys(plan.committedByEntity).reduce((cum, _) => cum + plan.committedByEntity[_], 0));
    const proposedWork = roundToHundreds(Object.keys(plan.proposedByEntity).reduce((cum, _) => cum + plan.proposedByEntity[_], 0));
    const actualWork = roundToHundreds(Object.keys(plan.actualByEntity).reduce((cum, _) => cum + plan.actualByEntity[_], 0));
    const availability = Math.max(0, capacityHours - committedWork);
    const overage = committedWork > capacityHours ? roundToHundreds(committedWork - capacityHours) : 0;
    const proposed_overage = (committedWork + proposedWork) > capacityHours ? roundToHundreds(committedWork + proposedWork - capacityHours) : 0;

    const proposedState = Object.keys(plan.proposedByEntity)
        .reduce((cum, entityId) => {
            const proposedOverageByEntity = (committedWork + plan.proposedByEntity[entityId]) > capacityHours
                ? roundToHundreds(committedWork + plan.proposedByEntity[entityId] - capacityHours)
                : 0;
            const hasLocalProposedOverageByEntity = overage > 0
                ? true
                : !!Object.keys(plan.dailyPlan)
                    .find(_ => roundToHundreds(plan.dailyPlan[_].committed + plan.dailyPlan[_].proposedByEntity[entityId])
                        > plan.dailyPlan[_].capacityHours);
            return {
                proposedOverageByEntity: { ...cum.proposedOverageByEntity, [entityId]: proposedOverageByEntity },
                hasLocalOverageByEntity: { ...cum.hasLocalOverageByEntity, [entityId]: hasLocalProposedOverageByEntity },
            };
        }, { proposedOverageByEntity: {} as Dictionary<number>, hasLocalOverageByEntity: {} as Dictionary<boolean> });

    return {
        fullFteCapacityHours,
        capacityHours,
        capacityByDay: Object.keys(dailyPlanWithDefaults).reduce<Dictionary<number>>((cum, _) => {
            cum[dailyPlanWithDefaults[_].date.getBeginOfDay().toISOString()] = dailyPlanWithDefaults[_].capacityHours;
            return cum;
        }, {}),
        committedWork,
        proposedWork,
        actualWork,

        committedByEntity: plan.committedByEntity,
        proposedByEntity: plan.proposedByEntity,
        actualByEntity: plan.actualByEntity,
        availability,
        overage,
        hasLocalOverage: overage > 0
            ? true
            : !!Object.keys(plan.dailyPlan)
                .find(_ => roundToHundreds(plan.dailyPlan[_].committed)
                    > plan.dailyPlan[_].capacityHours),

        estimatedCharge: roundToHundreds(plan.estimatedCharge),
        estimatedCost: roundToHundreds(plan.estimatedCost),
        actualCost: roundToHundreds(plan.actualCost),
        estimatedChargeByEntity: plan.estimatedChargeByEntity,
        estimatedCostByEntity: plan.estimatedCostByEntity,
        actualCostByEntity: plan.actualCostByEntity,

        effectiveUsage,
        effectiveUsageByEntity,

        proposed_overage,
        proposed_hasLocalOverage: proposed_overage > 0
            ? true
            : !!Object.keys(plan.dailyPlan)
                .find(_ => roundToHundreds(plan.dailyPlan[_].committed + plan.dailyPlan[_].proposed)
                    > plan.dailyPlan[_].capacityHours),
        proposed_overage_byEntity: proposedState.proposedOverageByEntity,
        proposed_hasLocalOverage_byEntity: proposedState.hasLocalOverageByEntity
    };
}

export const hasOutsideAllocation = memoizeFunction((usage: ResourceUsage | undefined, startDate: MaybeDate, finishDate: MaybeDate, entityId: string) => {
    const start = toDate(startDate)?.getBeginOfDay().getTime();
    const finish = toDate(finishDate)?.getEndOfDay().getTime();
    if (!startDate || !finishDate) {
        return false;
    }
    return !!findUsage(usage, entityId, _ => !!_.plannedHours && (!!start && (_.date.getTime() < start) || (!!finish && _.date.getTime() > finish)));
});

export const allocationWarning = (entityType: EntityType) => ({
    type: "allocation",
    text: `Resource allocation is outside the overall ${entityLogoConfig[entityType]?.label} dates range. 
    Please consider changing resource allocation or ${entityLogoConfig[entityType]?.label} dates.`
});